import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import './About.css';

const About = () => {
  const values = [
    {
      title: 'Reliability',
      description:
        'Reliability defines how we earn and maintain trust. Our clients know they can expect timely responses, transparent communication, and dependable execution every time they reach out. We approach every issue with consistency and attention to detail, ensuring technology problems are resolved without disrupting the flow of business. More than a promise, reliability is how we operate day to day—showing up prepared, following through, and providing the steady support our clients rely on to keep moving forward.',
      icon: '01'
    },
    {
      title: 'Ingenuity',
      description:
        'Ingenuity is how we turn complexity into clarity. When challenges don’t come with a clear playbook, we take a thoughtful, creative approach to uncover solutions that actually work in the real world. We focus on practical innovation—finding smarter, more efficient ways to improve systems without adding unnecessary complexity. This way of thinking allows us to adapt quickly, reduce friction, and deliver improvements that save time, streamline operations, and help our clients get more out of their technology.',
      icon: '02'
    },
    {
      title: 'Ownership',
      description:
        'Ownership is how we build trust that lasts. We take responsibility for our work from start to finish, approaching every engagement with accountability and care. Rather than reacting to problems, we stay proactive and invested in the outcomes, treating your technology as if it were our own. This sense of ownership allows us to support long-term goals, strengthen partnerships, and deliver results with consistency and confidence.',
      icon: '03'
    },
    {
      title: 'Teamwork',
      description:
        'Teamwork means working alongside our clients, not just for them. We take the time to understand how your business operates so our support fits naturally into your workflows and priorities. By acting as an extension of your team, we provide consistent collaboration during everyday operations and decisive support when urgent situations arise. This approach combines the familiarity of an internal IT department with the flexibility and expertise of a trusted partner, ensuring your technology continues to support your business at every stage.',
      icon: '04'
    }
  ];

  const timeline = [
    {
      year: '2017',
      title: 'Company Founded',
      description:
        'Snap Remedy was established with a mission to make enterprise IT services accessible to small and medium-sized businesses. Our founders, experienced IT professionals, saw the need for affordable, reliable technology solutions tailored to growing businesses.'
    },
    {
      year: '2018',
      title: 'Expanded from Residential Service to Business',
      description:
        'Recognizing the shift toward cloud computing, we expanded our services to include comprehensive cloud migration and management solutions. This marked a significant milestone as we helped hundreds of businesses transition to scalable cloud infrastructure.'
    },
    {
      year: '2019',
      title: 'Expanded Services Outside the Local Area',
      description:
        'We expanded our operations to serve clients across 35+ countries, establishing our reputation as a trusted global IT partner. We also enhanced our remote support capabilities, ensuring seamless service delivery regardless of location.'
    },
    {
      year: '2021',
      title: 'Rebrand With New Look and Feel and Website',
      description:
        'Today, we celebrate over 500 successful projects and continue to innovate with advanced security solutions, AI-powered monitoring, and next-generation cloud services. Our commitment to excellence drives us forward as we help businesses thrive in an increasingly digital world.'
    },
    {
      year: '2022',
      title: 'Expanded into new Industries like Manufacturing and Professional Services',
      description:
        'Today, we celebrate over 500 successful projects and continue to innovate with advanced security solutions, AI-powered monitoring, and next-generation cloud services. Our commitment to excellence drives us forward as we help businesses thrive in an increasingly digital world.'
    },
    {
      year: '2023',
      title: 'Partnered with World Class Security Solution Provider',
      description:
        'Today, we celebrate over 500 successful projects and continue to innovate with advanced security solutions, AI-powered monitoring, and next-generation cloud services. Our commitment to excellence drives us forward as we help businesses thrive in an increasingly digital world.'
    },
    {
      year: '2024',
      title: 'Added hosting and web design to our services',
      description:
        'Today, we celebrate over 500 successful projects and continue to innovate with advanced security solutions, AI-powered monitoring, and next-generation cloud services. Our commitment to excellence drives us forward as we help businesses thrive in an increasingly digital world.'
    },
    {
      year: '2025',
      title: 'Staffing increased due to high demand',
      description:
        'Today, we celebrate over 500 successful projects and continue to innovate with advanced security solutions, AI-powered monitoring, and next-generation cloud services. Our commitment to excellence drives us forward as we help businesses thrive in an increasingly digital world.'
    },
    {
      year: '2026',
      title: 'Another rebrand with new logo and website',
      description:
        'Today, we celebrate over 500 successful projects and continue to innovate with advanced security solutions, AI-powered monitoring, and next-generation cloud services. Our commitment to excellence drives us forward as we help businesses thrive in an increasingly digital world.'
    }
  ];

  return (
    <div className="about">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <motion.div className="about-hero-content">
            <div className="hero-label">
              <span className="label-dot"></span>
              About Us
            </div>
            <h1 className="hero-title">
              Accelerate Growth
              <span className="highlight">Through Technology</span>
            </h1>
            <p className="hero-description">
              Snap Remedy is a technology partner built to help businesses operate with confidence and momentum. We deliver reliable, innovative solutions that reduce complexity and support real-world workflows. From responsive IT support to secure infrastructure, automation, and integrations, we act as an extension of your team—keeping your technology efficient, dependable, and aligned with your growth.
            </p>
          </motion.div>
          <div>
            <img
              src="hero-snap-remedy-about.svg"
              className="hero-img"
              alt="Illustration of people and a big lightbulb"
            />
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="story-section section-spacing">
        <div className="container">
          <div className="story-grid">
            <motion.div className="story-image">
              <img
                src="jkiff-acalvano.png"
                className="hero-img"
                alt="Jason Kiff and Anthony Calvano, founders of Snap Remedy"
              />
            </motion.div>

            <motion.div className="story-content">
              <h2 className="section-title">Our Story</h2>
              <p className="story-text">
                Snap Remedy was founded on the belief that growing businesses deserve technology that works as hard as they do. We saw organizations across many industries facing the same challenges as larger enterprises—complex systems, security risks, inefficient workflows, and constant pressure to scale—without the internal teams or guidance to manage it all effectively. Our goal was to close that gap by becoming a true technology partner, not just a service provider. By combining dependable support, thoughtful design, automation, and secure infrastructure, we set out to help businesses make smarter technology decisions, move faster with confidence, and build systems that support long-term growth.
              </p>
              <p className="story-text">
                Since those early days, Snap Remedy has continued to evolve alongside our customers. We’ve refined our service offerings, expanded our capabilities, and rebranded along the way to more clearly reflect who we are and the value we bring. Each iteration has sharpened our focus on building long-term partnerships rooted in trust, reliability, and shared success. By listening closely to our clients and adapting to their changing needs, we’ve grown from solving individual problems to delivering cohesive technology solutions that support lasting relationships and sustainable growth.
              </p>
              <div className="story-stats">
                <div className="story-stat">
                  <div className="stat-number">60+</div>
                  <div className="stat-label">Years of Combined Experience</div>
                </div>
                <div className="story-stat">
                  <div className="stat-number">{'>90%'}</div>
                  <div className="stat-label">First Contact Resolution Rate</div>
                </div>
                <div className="story-stat">
                  <div className="stat-number">{'<5mins'}</div>
                  <div className="stat-label">Average Response Time</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section section-spacing">
        <div className="container">
          <motion.div className="section-header">
            <h2 className="section-title">Our Core Values</h2>
            <p className="section-description">
              Our core values are woven into everything we do at Snap Remedy. Reliability ensures our clients can depend on us when it matters most, ingenuity pushes us to find smarter and more effective ways to solve complex challenges, ownership means we take full responsibility for outcomes, and teamwork reflects how we work alongside our clients as an extension of their business. Together, these values guide our decisions, shape our relationships, and drive meaningful results. By staying grounded in these principles, we create technology solutions that not only work today but continue to support long-term success.
            </p>
          </motion.div>

          <div className="values-grid">
            {values.map((value, index) => (
              <motion.div key={index} className="value-card">
                <div className="value-icon">{value.icon}</div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="timeline-section section-spacing">
        <div className="container">
          <motion.div className="section-header">
            <h2 className="section-title">Our Journey</h2>
          </motion.div>

          <div className="timeline">
            {timeline.map((item, index) => (
              <motion.div key={index} className="timeline-item">
                <div className="timeline-year">{item.year}</div>
                <div className="timeline-content">
                  <h3 className="timeline-title">{item.title}</h3>
                  <p className="timeline-description">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta section-spacing">
        <div className="container">
          <motion.div className="cta-box">
            <h2 className="cta-title">Your Long-Term Partner for Smarter Technology</h2>
            <p className="cta-description">
              Snap Remedy exists to make technology dependable, practical, and aligned with the way your business actually works. From responsive IT support and security to automation, integration, and digital experiences, we bring reliability, ingenuity, ownership, and teamwork to everything we do. We don’t just solve problems—we build long-term partnerships, working alongside your team to simplify technology, strengthen operations, and support growth. If you're looking for a trusted technology partner who understands your business and shows up when it matters, we're ready to get started.
            </p>
            <div className="cta-actions">
              <Link to="/contact#contact-form" className="btn-cta-primary">
                Let’s Get Started
              </Link>
              <Link to="/solutions" className="btn-cta-secondary">
                Explore Solutions
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;